import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/auth";
import { CheckCircle2, XCircle, Loader2, Sparkles, RefreshCw } from "lucide-react";

// Polling strategy:
// - Up to 30 attempts × 2s = 1 minute of automatic polling.
// - 3 consecutive errors → bail out and show the manual "Recuperar mi plan" button
//   so the user is never stuck on an infinite spinner.
const MAX_ATTEMPTS = 30;
const POLL_INTERVAL = 2000;
const MAX_CONSECUTIVE_ERRORS = 3;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");
  const isFree = search.get("free") === "1";
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", plan: null, message: "Verificando tu pago…" });
  const [reclaiming, setReclaiming] = useState(false);
  const attemptsRef = useRef(0);
  const errorsRef = useRef(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    // Free 100%-discount path — no Stripe involved, already applied server-side.
    if (isFree) {
      (async () => {
        await refresh();
        setState({ status: "success", plan: "personal", message: "¡Plan activado!" });
      })();
      return;
    }

    if (!sessionId) {
      setState({
        status: "slow",
        message: "No encontramos el identificador de tu pago. Si te cobraron, pulsa 'Recuperar mi plan'.",
      });
      return;
    }

    const poll = async () => {
      if (cancelledRef.current) return;
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/payments/checkout/status/${sessionId}`);
        errorsRef.current = 0; // reset on every successful call
        const d = r.data;
        if (d.payment_status === "paid") {
          await refresh();
          setState({ status: "success", plan: d.plan, message: `¡Bienvenido al plan ${d.plan}!` });
          return;
        }
        if (d.status === "expired") {
          setState({
            status: "slow",
            message: "La sesión de pago ha expirado. Si te cobraron, pulsa 'Recuperar mi plan'.",
          });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({
            status: "slow",
            message: "Tu pago está tardando más de lo habitual. Si tu banco ya lo confirmó, pulsa 'Recuperar mi plan'.",
          });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        errorsRef.current += 1;
        // Treat 404 (transaction not found) as immediate failure — there's
        // nothing to wait for; show the manual reclaim path right away.
        if (err?.response?.status === 404) {
          setState({
            status: "slow",
            message: "No encontramos esta transacción. Si te cobraron, pulsa 'Recuperar mi plan'.",
          });
          return;
        }
        if (errorsRef.current >= MAX_CONSECUTIVE_ERRORS || attemptsRef.current >= MAX_ATTEMPTS) {
          setState({
            status: "slow",
            message: "No pudimos verificar tu pago automáticamente. Si ya pagaste, pulsa 'Recuperar mi plan'.",
          });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();
    return () => { cancelledRef.current = true; };
  }, [sessionId, refresh, isFree]);

  const handleReclaim = async () => {
    setReclaiming(true);
    try {
      const r = await api.post(`/payments/reclaim`);
      if (r.data?.recovered?.length > 0) {
        await refresh();
        setState({ status: "success", plan: r.data.plan, message: `¡Bienvenido al plan ${r.data.plan}!` });
      } else {
        setState({
          status: "not-found",
          message: "No encontramos ningún pago confirmado en Stripe a tu nombre en los últimos 7 días. Si crees que sí pagaste, escríbenos a support@keepsoul.app con tu email y el último 4 dígitos de la tarjeta.",
        });
      }
    } catch {
      setState({ status: "error", message: "No pudimos recuperar el pago. Escríbenos a support@keepsoul.app." });
    } finally {
      setReclaiming(false);
    }
  };

  const ReclaimBtn = () => (
    <button
      onClick={handleReclaim}
      disabled={reclaiming}
      className="btn-gold mt-4 inline-flex items-center gap-2 disabled:opacity-50"
      data-testid="payment-reclaim-btn"
    >
      {reclaiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
      {reclaiming ? "Buscando tu pago…" : "Recuperar mi plan"}
    </button>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-obsidian px-4">
      <div className="glass-card rounded-md p-12 max-w-md w-full text-center gold-glow fade-in">
        {state.status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-gold mx-auto mb-6 animate-spin" />
            <h1 className="font-display text-3xl mb-3 text-gold-light">{state.message}</h1>
            <p className="text-muted text-sm">No cierres esta ventana. Puede tardar hasta 1 minuto.</p>
          </>
        )}
        {state.status === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-gold mx-auto mb-6" strokeWidth={1.2} />
            <div className="font-label text-gold mb-3 flex items-center justify-center gap-2">
              <Sparkles className="w-3 h-3" /> ¡Pago confirmado!
            </div>
            <h1 className="font-display text-4xl mb-4">{state.message}</h1>
            <button
              onClick={() => navigate("/dashboard?payment=success")}
              className="btn-gold mt-4"
              data-testid="payment-success-cta"
            >
              Entrar al panel
            </button>
          </>
        )}
        {state.status === "slow" && (
          <>
            <Loader2 className="w-12 h-12 text-gold mx-auto mb-6" />
            <h1 className="font-display text-2xl mb-3">{state.message}</h1>
            <ReclaimBtn />
            <button onClick={() => navigate("/settings")} className="btn-ghost mt-2 block mx-auto" data-testid="payment-slow-back-btn">
              Ir a Ajustes
            </button>
          </>
        )}
        {state.status === "not-found" && (
          <>
            <XCircle className="w-14 h-14 text-amber-400 mx-auto mb-6" strokeWidth={1.2} />
            <h1 className="font-display text-xl mb-3">{state.message}</h1>
            <button onClick={() => navigate("/dashboard")} className="btn-ghost mt-4">
              Volver al panel
            </button>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto mb-6" strokeWidth={1.2} />
            <h1 className="font-display text-2xl mb-3">{state.message}</h1>
            <ReclaimBtn />
            <button onClick={() => navigate("/plans")} className="btn-ghost mt-2 block mx-auto">
              Volver a planes
            </button>
          </>
        )}
      </div>
    </div>
  );
}
